/**
 * The SESSION's conversation, across the runs that make it up.
 *
 * ── THE BUG THIS EXISTS FOR ──────────────────────────────────────────────────
 *
 * Ask a second question and the first question and its answer vanished from the screen.
 *
 * That is not a rendering slip, it is the shape of the protocol meeting a transcript that assumed
 * otherwise. `RunState` is per-RUN (contract §3.2), and `withUserTurn` says so in as many words:
 * "`RunState` is per-run, so there is exactly one exchange in it." Every question starts a new run,
 * `state.snapshot` REPLACES the store wholesale (§1.4 — it is the mandatory first durable frame on
 * every subscribe), and `WeaveTranscript` derived its turns from `RunState.messages` alone. So the
 * store was never wrong; it only ever described the current run, and the transcript was reading it
 * as if it described the conversation.
 *
 * The contract's own model is `Session` holds `turns` (§2.1), each served by a run (§2.3) — so the
 * conversation is a session-level fact that has to be assembled from the runs. `GET /api/sessions/{id}`
 * exists in §6, but the frontend handoff does not list it among the endpoints verified against the
 * deployed backend, and §GOTCHAS records several §6 endpoints that do not exist. Rather than depend
 * on one that may not answer, this keeps what the client already had in its hands: each run's own
 * messages, as they streamed, keyed by run id.
 *
 * ── THE ENDPOINT DOES EXIST; THE PATH ABOVE IS WRONG ─────────────────────────
 *
 * It hangs off a RUN: `GET /api/runs/{id}/session` returns the conversation as one `RunState` per
 * turn, and any run id in the session answers for the whole session. Looking for it under
 * `/api/sessions/{id}` is why it was written off as unverified — and that single wrong path is what
 * made every fidelity compromise downstream of it look necessary. `seedFromServer` now fills this
 * store from it (`sessionHistory.ts::archivedRunsFromSession`), and the local recording below is no
 * longer the source of truth for a past turn — it is what keeps the LIVE run archived until the
 * server's own account of it arrives.
 *
 * ── WHY A STORE AND NOT COMPONENT STATE ──────────────────────────────────────
 *
 * §5 of the frontend rules says a store only when the state must outlive the screen or be read from
 * an unrelated tree. This must outlive the RUN — which is exactly what `runStateStore.reset()` and
 * every fresh `state.snapshot` destroy — while staying scoped to one session.
 *
 * ── WHAT IT IS NOT ───────────────────────────────────────────────────────────
 *
 * Not a second copy of protocol state. It holds no `actions`, `plan`, `changeset`, `deploy` or
 * `evidence`: those are properties of the LIVE run and reading a stale one would be worse than not
 * showing it. Only `messages` and the user's own intent — the two things a past exchange is made of
 * — are kept, and the live run is always read from `runStateStore`, never from here.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { createOrgScopedJSONStorage, registerOrgScopedReset } from "@/lib/org-scope";
/*
 * ONE definition of an archived exchange, and it lives with the mapper that builds them from the
 * server's `session_runs`. Re-exported because this store is where consumers already import it
 * from, and a second declaration would be two shapes free to drift apart (§11).
 */
import type { ArchivedRun } from "@/services/multi-agent/sessionHistory";
import {
  DEFAULT_COMPOSER_MODE,
  isComposerMode,
  type ComposerMode,
} from "@/services/multi-agent/composerMode";
export type { ArchivedRun } from "@/services/multi-agent/sessionHistory";

/**
 * The run this client has just asked for, before the server has said anything about it.
 *
 * ── WHY THIS IS HERE AND NOT IN `runStateStore` ──────────────────────────────────────────────
 *
 * Between pressing Send and the first frame there is a window in which the ONLY record of what was
 * asked, and of which conversation it belongs to, is client-side: `createRun` has returned a run id,
 * and `RunState` does not exist yet. `runStateStore` is the protocol's mirror — every
 * `state.snapshot` REPLACES it wholesale (§1.4) — so a client-authored fact parked there is a fact
 * waiting to be overwritten by the next frame.
 *
 * It is also not a second copy of anything. `RunState.messages` used to carry assistant messages
 * only, leaving the user's own question with no protocol home at all; the backend now derives a
 * `role:"user"` message into every `RunState`, so a run the SERVER has described carries its own
 * question. This still owns the window before that exists — between `createRun` returning and the
 * first frame — which is the whole point of it, and the conversation is the thing that remembers
 * what was asked, which is why this store already persists per-org.
 *
 * ── WHAT IT REPLACES ─────────────────────────────────────────────────────────────────────────
 *
 * `multiAgentStore.startRun()` — the legacy store's `runId` + `sessionId` + `chat[0].text`. The v1
 * surface read all three from there, which is why the legacy store could not be deleted while v1
 * still ran. See the consolidation plan; this is step one of unpicking that.
 */
export interface CurrentRun {
  runId: string;
  /** `null` only for the very first run of a brand-new conversation, before the server names one. */
  sessionId: string | null;
  /** What the user typed to start it. Fixed for the life of the run. */
  intent: string;
}

interface WeaveSessionStore {
  /** Which session these runs belong to. A different one clears the list rather than mixing two. */
  sessionId: string | null;
  runs: ArchivedRun[];
  /** The live run's client-side identity. `null` when no run has been started from this client. */
  current: CurrentRun | null;
  /**
   * The composer's Read / Write / Plan selection.
   *
   * HERE, NOT IN COMPONENT STATE, because it must outlive a reload and a route change: a reader
   * who chose Plan expects the next message after a refresh to still plan. It is composer
   * preference, not run state — nothing in `RunState` says what the reader will send next — and
   * this store is already the persisted, org-scoped record of "this client's conversation", which
   * is exactly the scope the preference has. Sticky across turns; `composerMode.ts` owns the one
   * case where it is applied and the one where it is not.
   */
  composerMode: ComposerMode;
  setComposerMode: (mode: ComposerMode) => void;
  /**
   * Record (or update) one run's contribution.
   *
   * Idempotent per `runId`, and called while the run is still live: the entry is kept current as
   * frames arrive, so nothing has to detect "the run finished" to archive it — a detection that
   * would lose the exchange whenever a run ended in a way nobody anticipated.
   */
  record: (sessionId: string | null, run: ArchivedRun) => void;
  /**
   * The client has started a run: remember what was asked and which conversation it belongs to.
   *
   * Called once per `createRun`, from the one place that creates runs. Starting a run in a DIFFERENT
   * session clears the archive for the same reason `record` keys on it — two conversations must not
   * stack.
   */
  begin: (run: CurrentRun) => void;
  /**
   * Open a conversation this client did not start — the history drawer, the session sidebar.
   *
   * DISTINCT FROM `begin`, and the difference is who authored the conversation. `begin` records a
   * question this client just asked, so it always has an intent. Adopting is the opposite: the
   * reader is stepping into an existing conversation whose exchanges live on the server, and
   * claiming an intent here would put words in the transcript that nobody typed.
   *
   * Switching to a DIFFERENT session drops the archive and the current run, because both describe
   * the conversation being left. Re-opening the SAME session is a no-op: the reader clicked the chat
   * they are already in, and clearing would throw away exchanges that are still on screen.
   */
  adopt: (sessionId: string | null) => void;
  /**
   * Replace the archive with the SERVER's account of this conversation.
   *
   * ── THIS IS WHAT MAKES THE BACKEND THE SOURCE OF TRUTH ───────────────────────────────────
   *
   * `runs` used to be the only record of a conversation's past exchanges, written by this browser
   * as it streamed them. A conversation opened from the drawer — or started in another tab, on
   * another machine, or before the profile was cleared — had no entries, so the transcript showed a
   * single exchange and called it the conversation.
   *
   * `GET /api/runs/{id}/detail` already answers with `session_runs[]`: every run in the session with
   * its question and its answer. Both routes into a conversation already fetch it. So the archive
   * stops being a record and becomes a CACHE of that response, replaced wholesale on every entry —
   * which is why this takes the full list rather than merging: a merge would preserve local entries
   * the server no longer has (a deleted run, a run from a conversation the reader has left).
   *
   * It is still a cache worth having. Between entries the reader asks follow-ups, and `record` keeps
   * those exchanges on screen the moment they finish rather than waiting for a refetch that would
   * tell the client what it already watched happen.
   */
  seedFromServer: (sessionId: string | null, runs: ArchivedRun[], current?: CurrentRun | null) => void;
  /** Drop everything — a new session, or the reader explicitly starting over. */
  clear: () => void;
}

const PERSIST_NAME = "weave-session-v1";

/** Keeps the archive bounded; a session of 40 exchanges is already past what anyone scrolls. */
const MAX_ARCHIVED_RUNS = 40;

export const useWeaveSessionStore = create<WeaveSessionStore>()(
  persist(
    (set) => ({
      sessionId: null,
      runs: [],
      current: null,
      composerMode: DEFAULT_COMPOSER_MODE,
      // Validated, because the value also arrives from persisted storage a future version may have
      // written with a vocabulary this build does not know.
      setComposerMode: (mode) => set({ composerMode: isComposerMode(mode) ? mode : DEFAULT_COMPOSER_MODE }),
      begin: (run) =>
        set((state) => ({
          // A run started in another session is another conversation: keep the identity, drop the
          // archive. Same rule as `record`, so the two cannot disagree about what "this session" is.
          sessionId: run.sessionId ?? state.sessionId,
          runs: state.sessionId && run.sessionId && state.sessionId !== run.sessionId ? [] : state.runs,
          current: run,
        })),
      adopt: (sessionId) =>
        set((state) => {
          if (!sessionId || sessionId === state.sessionId) return state;
          return { sessionId, runs: [], current: null };
        }),
      seedFromServer: (sessionId, runs, current) =>
        set((state) => {
          /*
           * ── A SEED MUST NOT WIPE A RUN THIS CLIENT RECORDED ITSELF ─────────────────────────
           *
           * THE BUG, measured in the route harness: open a past conversation, then ask a follow-up,
           * and the turn you had just been looking at lost its question — it came back reading as
           * the follow-up, appearing twice.
           *
           * The server's history deliberately EXCLUDES the run being opened, because that one is
           * live and renders from `runStateStore`. `WeaveTranscript` archives it as it streams, so
           * it is in `runs` as a locally recorded entry. This used to replace `runs` outright — so a
           * second seed landing after that (the drawer's open and the auto-load's restore both seed,
           * and either can finish last) deleted the live run's only archived copy. When the
           * follow-up then made it history, `record` created the entry FRESH, and a fresh entry has
           * no intent to preserve — so it took `current.intent`, which by then was the follow-up's
           * question. The store's own note below explains why an existing intent is never
           * overwritten; this is the same invariant failing one step earlier, because the entry it
           * protects had been removed.
           *
           * So the server's turns are merged over what is here rather than replacing it: anything
           * the server knows about wins (it is authoritative and richer), and anything only this
           * client knows about — the live run — is kept, in order, after them.
           *
           * Only within ONE conversation. Entering a different one is `adopt`'s job and it clears
           * the archive; preserving across that would stitch two chats together.
           */
          const sameConversation = !sessionId || !state.sessionId || sessionId === state.sessionId;
          const seeded = new Set(runs.map((r) => r.runId));
          const kept = sameConversation ? state.runs.filter((r) => !seeded.has(r.runId)) : [];
          const merged = [...runs, ...kept];
          return {
            sessionId: sessionId ?? state.sessionId,
            runs: merged.slice(-MAX_ARCHIVED_RUNS),
            /*
             * THE LIVE RUN'S QUESTION COMES FROM THE SERVER TOO, when entering a conversation.
             *
             * `current` normally holds a question THIS client typed. Opening an existing conversation
             * has no such question — `adopt` just cleared it — and the newest run is somebody's
             * earlier turn, whose text is `detail.run.user_intent`. Without this the conversation
             * rendered every exchange except its most recent one, which was the one the reader
             * clicked on: the archive deliberately excludes the live run, and nothing else knew what
             * it had asked.
             *
             * Only ever passed on ENTRY, where there is no local run in flight — a seed arriving
             * mid-stream would overwrite the question the reader just typed with the server's older
             * one. Callers that are not entering a conversation omit it and keep what is there.
             */
            current: current === undefined ? state.current : current,
          };
        }),
      record: (sessionId, run) =>
        set((state) => {
          /*
           * ── A WRITE FOR A CONVERSATION WE HAVE LEFT CANNOT REOPEN IT ──────────────────────
           *
           * THE RACE, caught by the route harness driving the real history drawer: opening chat two
           * calls `adopt("sess_two")`, which clears the archive — but `WeaveTranscript` is still
           * mounted and `runStateStore` still holds chat ONE's run until the new subscribe lands.
           * Its archiving effect fires in that window and called `record("sess_one", run_1)`, which
           * used to re-key the whole store: session snapped back to `sess_one` and chat one's
           * exchange reappeared under chat two. Measured: session `sess_two` → `sess_one`, archive
           * 0 → 1, with no user action in between.
           *
           * So `record` no longer DECIDES which conversation is open. `begin` (this client asked a
           * question) and `adopt` (the reader opened a chat) are the only two ways, and a record for
           * any other session is a stale echo of the run being torn down — dropped.
           *
           * The `!state.sessionId` case still adopts: a brand-new conversation calls `begin` before
           * the server has named a session, so the protocol's first snapshot is where the id comes
           * from. Without that, the very first exchange of every new chat would be dropped.
           */
          if (state.sessionId && sessionId && sessionId !== state.sessionId) return state;
          const base = state.sessionId === sessionId ? state.runs : [];
          const at = base.findIndex((r) => r.runId === run.runId);
          /*
           * ── THE INTENT IS FIXED WHEN THE RUN STARTS; ONLY MESSAGES MOVE ─────────────────────
           *
           * THE BUG THIS FIXES: asking a second question REWROTE the first question. "Test" turned
           * into "Give me implementation", and that text then appeared above both answers.
           *
           * The two halves of an entry arrive from different places at different moments. Messages
           * come from `runStateStore`, which does not advance to the new run until its snapshot
           * lands. The intent comes from the legacy store's `chat`, which `startRun()` clears and
           * repopulates the instant the user presses Send. So between Send and the first frame there
           * is a window where `runId` still names the PREVIOUS run while `userIntent` already names
           * the NEW question — and a blind overwrite stamped the new question onto the old run.
           *
           * A run's intent cannot change: it is what was asked to start it. So an existing, non-null
           * intent is never replaced, and only `messages` are updated. The window still exists, it
           * just no longer corrupts anything — the new run records its own intent when it appears.
           */
          const next =
            at === -1
              ? [...base, run]
              : base.map((r, i) =>
                  i === at ? { ...run, userIntent: r.userIntent ?? run.userIntent } : r,
                );
          return {
            sessionId,
            runs: next.length > MAX_ARCHIVED_RUNS ? next.slice(next.length - MAX_ARCHIVED_RUNS) : next,
          };
        }),
      clear: () => set({ sessionId: null, runs: [], current: null }),
    }),
    {
      name: PERSIST_NAME,
      /*
       * Org-scoped storage, so one org's conversation cannot be read back under another (§6 treats
       * this as a security boundary). The in-memory copy is dropped on a switch by the reset below —
       * namespacing the key alone fixes reloads, not switches.
       */
      storage: createOrgScopedJSONStorage(PERSIST_NAME),
      version: 1,
      /*
       * ── WHAT IS PERSISTED, AND WHY `runs` IS NOT ────────────────────────────────────────────
       *
       * `current` is: it is CLIENT-AUTHORED and unrecoverable from the server. `RunState.messages`
       * carries assistant messages only (§3.2), so the question the reader typed exists nowhere but
       * here until the run finishes — dropping it on reload would leave an answer with nothing above
       * it, mid-stream.
       *
       * `runs` is NOT, and that is a deliberate reversal. Every entry in it is a copy of something
       * `GET /api/runs/{id}/detail` returns in `session_runs`, and a copy in localStorage is a copy
       * that can be stale, can outlive a deleted run, and is invisible to any other tab. The server
       * is the source of truth for a conversation's past; this store caches it for the session and
       * re-seeds from the response on every entry. `sessionId` stays so a reload knows which
       * conversation to ask about.
       */
      partialize: (state) => ({
        sessionId: state.sessionId,
        current: state.current,
        composerMode: state.composerMode,
      }),
      /*
       * A persisted mode outside today's vocabulary (a renamed mode, a hand-edited key) falls back
       * to the default rather than putting an unknown string on the wire as `mode_hint`.
       */
      merge: (persisted, current) => {
        const stored = (persisted ?? {}) as Partial<WeaveSessionStore>;
        return {
          ...current,
          ...stored,
          composerMode: isComposerMode(stored.composerMode) ? stored.composerMode : DEFAULT_COMPOSER_MODE,
        };
      },
    },
  ),
);

registerOrgScopedReset(PERSIST_NAME, () => useWeaveSessionStore.getState().clear());

export const selectArchivedRuns = (s: WeaveSessionStore): ArchivedRun[] => s.runs;

/** The live run's client-side identity — what was asked, and in which conversation. */
export const selectCurrentRun = (s: WeaveSessionStore): CurrentRun | null => s.current;
/** What the user asked to start the live run, for the transcript's own user turn. */
export const selectCurrentIntent = (s: WeaveSessionStore): string | null => s.current?.intent ?? null;
export const selectComposerMode = (s: WeaveSessionStore): ComposerMode => s.composerMode;
/** The conversation this client is in — the `X-Session-ID` every run and stream is tagged with. */
export const selectSessionId = (s: WeaveSessionStore): string | null =>
  s.current?.sessionId ?? s.sessionId;
